import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import MainWrapper from '../wrappers/mainWrapper';
import ExpandedMatchBlock from '../restructured/ExpandedMatchBlockpdf';
import { useParams } from 'react-router-dom';
import '../../css/restructuredTransitions.css';
import PDFViewer from '../restructured/PDFViewer';
import Filters from '../restructured/Filters';
import NukeButton from '../restructured/NukeButton';
import ExportView from '../restructured/ExportView';
import exportPDFIcon from '../restructured/assets/Export-PDF.jpg';
import '../../css/App.css';
import stopIcon from '../../img/_Stop.png';
import yieldIcon from '../../img/_Yield.png';
import goIcon from '../../img/_Go.png';
import closeIcon from '../../img/pretty-close-2.png';

const RestructuredResultspdf = () => {
  const [data, setData] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState('loading-spinner');
  const { comparisonID } = useParams();
  const [visibleDocs, setVisibleDocs] = useState({ doc1: false, doc2: false });
  const [designationFilter, setDesignationFilter] = useState('All');
  const userData = useSelector(state => state.user);

  const [logoUrl, setLogoUrl] = useState(null);
  const [error, setError] = useState(null);

  const uploadUrl = process.env.REACT_APP_UPLOAD_LOGO;

  useEffect(() => {
    async function fetchLogoUrl() {
      try {
        const res = await fetch(`${process.env.REACT_APP_GET_LOGO}/${userData._id}`, {
          credentials: 'include', // if needed
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Failed to fetch logo URL');
        setLogoUrl(json.logoUrl);
      } catch (err) {
        setError(err.message);
      }
    }

    fetchLogoUrl();
  }, [userData]);

  const [validLogo, setValidLogo] = useState(null);

  useEffect(() => {
    if (!logoUrl) {
      setValidLogo(null);
      return;
    }

    const img = new Image();
    img.src = logoUrl;
    img.onload = () => setValidLogo(logoUrl);
    img.onerror = () => setValidLogo(null);

    // cleanup
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [logoUrl]);

  const toggleDoc = (docKey) => {
    setVisibleDocs((prev) => ({ ...prev, [docKey]: !prev[docKey] }));
  };

  const setCleanParsedEstData = (updated) => {
    setData((prev) => (prev ? { ...prev, cleanParsedEstData: updated } : prev));
  };

  const setCleanParsedInsData = (updated) => {
    setData((prev) => (prev ? { ...prev, cleanParsedInsData: updated } : prev));
  };

  const saveResults = async (resultsID, updatedResults) => {
    if (!data) return;
    const fullUpdatedData = { ...data, results: updatedResults };
    try {
      const response = await fetch(`${process.env.REACT_APP_UPDATE_RESULTS}/${resultsID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: fullUpdatedData }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to save results');
    } catch (err) {
      console.error('Error saving results:', err);
    }
  };

  const fetchData = async () => {
    try {
      const compResponse = await fetch(`${process.env.REACT_APP_RESULTS_DATA}comp/${comparisonID}`);
      const comp = await compResponse.json();
      const splitResponse = await fetch(`${process.env.REACT_APP_SPLIT_RESULTS}/${comp.resultsID}`);
      const split = await splitResponse.json();
      const resultsData = typeof split.data === 'string' ? JSON.parse(split.data) : split.data;

      const newData = {
        results: resultsData.results,
        claimData: comp.claimData,
        files: comp.files,
        headers: comp.headers,
        resultsID: comp.resultsID,
        dateUploaded: comp.dateUploaded,
        statusMSG: comp.statusMSG,
        status: comp.status,
        doc1_name: comp.doc1_name,
        doc2_name: comp.doc2_name,
        estPriceList: comp.estPriceList,    
        insPriceList: comp.insPriceList,
        compID: comp.compID,
        headersOne: comp.headersOne,
        headersTwo: comp.headersTwo,
        missingOne: comp.missingOne,
        missingTwo: comp.missingTwo,
        cleanParsedEstData: comp.cleanParsedEstData,
        cleanParsedInsData: comp.cleanParsedInsData,
      };

      setLoadingStatus('');
      setData(newData);
    } catch (err) {
      console.error('Error fetching results data:', err);
      setLoadingStatus('Error loading data');
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 4000);
    return () => clearInterval(intervalId);
  }, [comparisonID]);

  // ----- PDF Export function using POST form to avoid popup blockers -----
  const openPdfInNewTab = () => {
    if (!data) return alert('No data to export');

    // You need to create your HTML string here from data
    // For demo, minimal html string with JSON stringified data:
    const htmlContent = generateHtmlFromData(data);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/'; 
    form.target = '_blank';

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'html';
    input.value = htmlContent;

    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  // Placeholder function: customize this to produce your actual export HTML from data
  const generateHtmlFromData = (data) => {
    // Example: you would produce the same HTML structure your backend expects
    // For now, just output JSON wrapped in preformatted text:
    return `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h1>Exported Results</h1>
        <pre>${JSON.stringify(data.results, null, 2)}</pre>
      </div>
    `;
  };

  return (
    <div style={{ overflow: 'hidden' }}>
      <div
        id="pdf-scale-wrapper"
        style={{
          transform: 'scale(0.75)',
          transformOrigin: 'top left',
          width: '133.33%',
          margin: 0,
          padding: 0,
        }}
      >
        <br data-pdf-ignore />
        <br data-pdf-ignore />
        <div
          className="form-header"
          style={{ marginBottom: '20px', marginLeft: '10px', display: 'flex', alignItems: 'center', gap: '20px' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Title */}
            <div style={{ fontSize: 35, fontStyle: 'italic', fontWeight: 700, color: '#4a4a4a', lineHeight: 1.15, marginBottom: 10 }}>
              Insured Name: {data?.claimData?.insured || 'N/A'}&nbsp;&nbsp; Claim #: {data?.claimData?.claim_number || 'N/A'}
            </div>

            {/* Address (no label in mock) */}
            <div style={{ fontSize: 18, fontStyle: 'italic', color: '#6f6f6f', lineHeight: 1.25 }}>
              {data?.claimData?.insured_address || 'N/A'}
            </div>

            {/* Adjuster */}
            <div style={{ fontSize: 18, fontStyle: 'italic', color: '#6f6f6f', lineHeight: 1.25 }}>
              Adjuster: {data?.claimData?.adjuster || 'N/A'}
            </div>

            {/* KEY row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 20, marginBottom: 8, fontSize: 19, fontStyle: 'normal', color: '#222' }}>
              <span style={{ fontWeight: 700, color: '#4a4a4a', marginLeft: 100 }}>KEY:</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <img src={goIcon} alt="" style={{ width: 35, height: 35, objectFit: 'contain' }} /> Perfect Match Found
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <img src={closeIcon} alt="" style={{ width: 35, height: 35, objectFit: 'contain' }} /> Match Found
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <img src={yieldIcon} alt="" style={{ width: 35, height: 35, objectFit: 'contain' }} /> Similar Item Found
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <img src={stopIcon} alt="" style={{ width: 35, height: 35, objectFit: 'contain' }} /> No Match Found
              </span>
            </div>

            {/* DOC 1 / DOC 2 */}
            <div style={{ fontSize: 18, fontStyle: 'italic', color: '#000', marginTop: 12 }}>
            <div style={{ marginTop: 6 }}>

              <span style={{ fontWeight: 700, color: '#000' }}>DOC 1:</span>&nbsp;
              <span style={{ fontWeight: 700, color: '#000' }}>
                {data?.doc1_name || 'N/A'}
              </span>
              &nbsp; |&nbsp;
              <span style={{ fontStyle: 'italic', color: '#000' }}>
                PriceList: {data?.estPriceList || 'N/A'}
              </span>
            </div>
            <div style={{ marginTop: 6 }}>
              <span style={{ fontWeight: 700, color: '#000' }}>DOC 2:</span>&nbsp;
              <span style={{ fontWeight: 700, color: '#000' }}>
                {data?.doc2_name || 'N/A'}
              </span>
              &nbsp; |&nbsp;
              <span style={{ fontStyle: 'italic', color: '#000' }}>
                PriceList: {data?.insPriceList || 'N/A'}
              </span>
            </div>
          </div>


          </div>


          {/* Logo right side */}
          {validLogo ? <img style={{maxHeight: '170px', width: 'auto'}} src={validLogo} /> : null}
        </div>

        <div>
          <div className={loadingStatus}></div>
          {data?.results
            ?.flatMap((result) =>
              result.Matches.map((matchObj, idx) => {
                const matchKey = Object.keys(matchObj)[0];
                const match = matchObj[matchKey];
                const marked = match?.['Mark For Export'] === true || match?.['Mark For Export'] === 'true';
                const designation = match?.['User Modified']?.Designation || 'No-Match';
                const designationPass = designationFilter === 'All' || designation === designationFilter;
                if (!marked || !designationPass) return null;
                return {
                  resultId: result.id,
                  match,
                  key: `${result.id}-${idx}`,
                };
              }).filter(Boolean)
            )
            .map(({ resultId, match, key }) => (
              <div className="comparison-block" key={key}>
                <ExpandedMatchBlock
                  match={match}
                  cleanParsedEstData={data.cleanParsedEstData}
                  setCleanParsedEstData={setCleanParsedEstData}
                  cleanParsedInsData={data.cleanParsedInsData}
                  setCleanParsedInsData={setCleanParsedInsData}
                  headersOne={data.headersOne}
                  headersTwo={data.headersTwo}
                  docname1={data.doc1_name}
                  docname2={data.doc2_name}
                  claimId={data.compID}
                  visibleDocs={visibleDocs}
                  toggleDoc={toggleDoc}
                  // onToggle={() => toggleExpanded(resultId)} // Not defined here
                  // onSaveMatchUpdate={(u) => saveMatchResults(resultId, u)} // Not defined here
                  // onSaveMatchNotes={(n) => saveMatchNotes(resultId, n)} // Not defined here
                  // onSaveMarkForExport={(v) => saveMarkForExport(resultId, v)} // Not defined here
                  // onRevertMatch={() => revertMatch(resultId)} // Not defined here
                  resultsID={data.resultsID}
                />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default RestructuredResultspdf;
